import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type ApiError, type ListBulkJobsResult } from '@whinerp/client';
import { EmptyState, InlineAction, Num, Panel, SectionLabel, Skeleton } from '@whinerp/ui';
import { formatCount } from '@whinerp/contracts/format';
import { useErrorMessage, useFormat, useT } from '../../app/i18n.js';

/**
 * Setup > Developer > Bulk jobs (F-126).
 *
 * The list an ops team watches while forty thousand rows go in. Two things it
 * has to do that a progress bar usually does not.
 *
 * **Say the numbers.** "Running" with a moving bar and no figures cannot
 * answer "how far, and how much longer" - and a bar driven by elapsed time
 * sits at 90% for an hour.
 *
 * **Tell slow from dead.** A job whose worker died looks exactly like one that
 * is working hard, unless something says so. `stalled` says so, and offers the
 * way to pick it up.
 */
export function BulkPanel(): JSX.Element {
  const t = useT();
  const format = useFormat();
  const errorMessage = useErrorMessage();
  const client = useQueryClient();
  const [errorFile, setErrorFile] = useState<{ fileName: string; csv: string } | null>(null);

  const jobs = useQuery<ListBulkJobsResult, ApiError>({
    queryKey: ['bulk-jobs'],
    queryFn: () => api.listBulkJobs({}),
    /*
     * Polls while anything is unfinished, and stops when nothing is.
     * A list that keeps asking after every job has settled is a request every
     * few seconds for a page nobody is watching change.
     */
    refetchInterval: (query) =>
      (query.state.data?.jobs ?? []).some((job) => job.finishedAt === null) ? 2_000 : false,
  });

  const refresh = (): void => {
    void client.invalidateQueries({ queryKey: ['bulk-jobs'] });
  };

  const resume = useMutation({
    mutationFn: (jobId: string) => api.resumeBulkJob({ path: { jobId } }),
    onSuccess: refresh,
  });
  const stop = useMutation({
    mutationFn: (jobId: string) => api.cancelBulkJob({ path: { jobId } }),
    onSuccess: refresh,
  });
  const errors = useMutation({
    mutationFn: (jobId: string) => api.bulkJobErrors({ path: { jobId } }),
    onSuccess: (file) => setErrorFile({ fileName: file.fileName, csv: file.csv }),
  });

  // A skeleton means "still loading". Rendering one on error is a spinner that
  // never resolves.
  if (jobs.isPending) return <Skeleton className="h-block-sm w-full" />;
  if (jobs.isError) return <></>;

  return (
    <Panel data-region="bulk" id="bulk">
      <div className="flex flex-col gap-3">
        <SectionLabel>{t('bulk.title')}</SectionLabel>
        <p className="text-meta text-ink-meta">{t('bulk.body')}</p>
        <p className="text-meta text-ink-meta" data-bulk-quota>
          {jobs.data.rowLimit === null
            ? t('bulk.noLimit')
            : t('bulk.rowLimit', {
                values: { limit: formatCount(jobs.data.rowLimit, format) },
              })}
        </p>

        {jobs.data.jobs.length === 0 ? (
          <EmptyState
            data-bulk-empty
            illustration="Stack"
            title={t('bulk.title')}
            body={t('bulk.empty')}
            primary={{ label: t('developer.contract'), icon: 'FileText', href: '/setup#developer' }}
          />
        ) : (
          <ul className="flex flex-col gap-2" data-bulk-jobs>
            {jobs.data.jobs.map((job) => (
              <li
                key={job.jobId}
                data-bulk-job={job.jobId}
                data-bulk-status={job.status}
                data-bulk-stalled={job.stalled}
                className="flex flex-wrap items-center gap-2 rounded-chip bg-sunken px-3 py-2"
              >
                <span className="text-body text-ink">
                  {job.operation} {job.objectKey}
                </span>
                <span className="text-meta text-ink-meta">
                  {t(`bulk.status.${job.status}` as 'bulk.status.queued')}
                </span>
                {/* The numbers, not only the bar. */}
                <span className="text-meta text-ink-meta" data-bulk-progress>
                  {t('bulk.progress', {
                    values: {
                      done: formatCount(job.processedRows, format),
                      total: formatCount(job.totalRows, format),
                    },
                  })}
                </span>
                <Num data-bulk-percent className="text-meta text-ink-meta">
                  {`${formatCount(job.percent, format)}%`}
                </Num>
                {job.failedRows > 0 ? (
                  <InlineAction data-bulk-errors={job.jobId} onClick={() => errors.mutate(job.jobId)}>
                    {t('bulk.failed', { values: { count: formatCount(job.failedRows, format) } })}
                  </InlineAction>
                ) : null}
                {job.stalled ? (
                  <>
                    {/* Said, not left to be inferred from a bar that stopped. */}
                    <span data-bulk-stalled-note className="text-meta text-warning-text">
                      {t('bulk.stalled')}
                    </span>
                    <InlineAction
                      data-bulk-resume={job.jobId}
                      onClick={() => resume.mutate(job.jobId)}
                    >
                      {t('bulk.resume')}
                    </InlineAction>
                  </>
                ) : null}
                {job.finishedAt === null ? (
                  <InlineAction data-bulk-cancel={job.jobId} onClick={() => stop.mutate(job.jobId)}>
                    {t('bulk.cancel')}
                  </InlineAction>
                ) : null}
              </li>
            ))}
          </ul>
        )}

        {errors.isError ? (
          <p role="alert" className="text-meta text-danger">
            {errorMessage(errors.error)}
          </p>
        ) : null}
        {/*
          The error file, on screen.

          The viewer cannot be handed a download, so it is shown selectable
          instead - which is honest about what this surface can do. The API
          returns the same CSV to anything that can save a file.
        */}
        {errorFile ? (
          <div className="flex flex-col gap-1" data-bulk-error-file>
            <SectionLabel>{errorFile.fileName}</SectionLabel>
            <pre className="max-h-block-md select-all overflow-auto rounded-chip bg-surface p-2 text-dense text-ink">
              {errorFile.csv}
            </pre>
            <div>
              <InlineAction data-bulk-error-close onClick={() => setErrorFile(null)}>
                {t('action.cancel')}
              </InlineAction>
            </div>
          </div>
        ) : null}
      </div>
    </Panel>
  );
}
